import React, { useEffect, useState } from 'react';
import { FocusedLayout } from '../../components/layout/FocusedLayout';
import { userAPI, publicAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { copyToClipboard, shareVoucher, formatDate } from '../../utils/helpers';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { Copy, Share2, Ticket, Sparkles } from 'lucide-react';

export const VouchersPage = () => {
  const [vouchers, setVouchers] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buyOpen, setBuyOpen] = useState(false);
  const [buyForm, setBuyForm] = useState({ currency: '', value: '', expiry_days: 365 });
  const [newVoucher, setNewVoucher] = useState(null);
  const [buying, setBuying] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [vRes, cRes, wRes] = await Promise.all([
        userAPI.getVouchers(),
        publicAPI.getCurrencies(true),
        userAPI.getWallets()
      ]);
      setVouchers(vRes.data);
      setCurrencies(cRes.data);
      setWallets(wRes.data);
    } catch (error) {
      toast.error('Failed to load');
    } finally { setLoading(false); }
  };

  const handleCopy = async (code) => {
    const ok = await copyToClipboard(code);
    if (ok) toast.success('Voucher code copied!');
  };

  const handleShare = async (code, value, currency) => {
    await shareVoucher(code, value, currency);
  };

  const openBuy = () => {
    setBuyForm({ currency: wallets[0]?.currency || '', value: '', expiry_days: 365 });
    setBuyOpen(true);
  };

  const submitBuy = async (e) => {
    e.preventDefault();
    if (!buyForm.currency || !buyForm.value || parseFloat(buyForm.value) <= 0) {
      toast.error('Please fill all fields with valid values');
      return;
    }
    setBuying(true);
    try {
      const { data } = await userAPI.buyVoucher(buyForm.currency, parseFloat(buyForm.value), parseInt(buyForm.expiry_days));
      toast.success('Voucher purchased successfully!');
      setBuyOpen(false);
      setNewVoucher(data);
      load();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Purchase failed');
    } finally { setBuying(false); }
  };

  const activeVouchers = vouchers.filter(v => v.status === 'active');
  const usedVouchers = vouchers.filter(v => v.status !== 'active');

  const renderVoucherCard = (v) => (
    <Card key={v.id} data-testid={`voucher-${v.id}`} className="hover:shadow-lg transition-shadow">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Ticket className="h-5 w-5 text-blue-600" />
            <CardTitle className="text-lg">{v.value} {v.currency}</CardTitle>
          </div>
          <Badge className={v.status === 'active' ? 'bg-green-600' : 'bg-gray-500'}>
            {v.status}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="p-3 rounded-lg bg-muted font-mono text-sm break-all">{v.code}</div>
        <div className="text-xs text-muted-foreground space-y-1">
          <p>Created: {formatDate(v.created_at)}</p>
          <p>Expires: {formatDate(v.expires_at)}</p>
          {v.redeemed_at && <p>Redeemed: {formatDate(v.redeemed_at)}</p>}
        </div>
        {v.status === 'active' && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="flex-1" onClick={() => handleCopy(v.code)} data-testid={`copy-voucher-${v.id}`}>
              <Copy className="h-3 w-3 mr-1" /> Copy
            </Button>
            <Button size="sm" variant="outline" className="flex-1" onClick={() => handleShare(v.code, v.value, v.currency)} data-testid={`share-voucher-${v.id}`}>
              <Share2 className="h-3 w-3 mr-1" /> Share
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <FocusedLayout title="My Vouchers" description="View and manage your vouchers">
      <div className="space-y-6" data-testid="vouchers-page">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>My Vouchers</h1>
            <p className="mt-2 text-muted-foreground">Buy, manage and share vouchers instantly</p>
          </div>
          <Button onClick={openBuy} data-testid="buy-voucher-button" className="gap-2">
            <Sparkles className="h-4 w-4" /> Buy Voucher
          </Button>
        </div>

        <Tabs defaultValue="active">
          <TabsList>
            <TabsTrigger value="active">Active ({activeVouchers.length})</TabsTrigger>
            <TabsTrigger value="used">Used/Expired ({usedVouchers.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="active" className="mt-6">
            {loading ? <p className="text-center py-8">Loading...</p> : activeVouchers.length === 0 ? (
              <Card><CardContent className="py-12 text-center space-y-3">
                <Ticket className="h-12 w-12 mx-auto text-muted-foreground" />
                <p className="text-muted-foreground">No active vouchers. Click "Buy Voucher" to create one!</p>
              </CardContent></Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{activeVouchers.map(renderVoucherCard)}</div>
            )}
          </TabsContent>
          <TabsContent value="used" className="mt-6">
            {usedVouchers.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-muted-foreground">No used vouchers yet</CardContent></Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{usedVouchers.map(renderVoucherCard)}</div>
            )}
          </TabsContent>
        </Tabs>

        <Dialog open={buyOpen} onOpenChange={setBuyOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Buy a Voucher</DialogTitle></DialogHeader>
            <form onSubmit={submitBuy} className="space-y-4">
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select value={buyForm.currency} onValueChange={(v) => setBuyForm({...buyForm, currency: v})}>
                  <SelectTrigger data-testid="buy-voucher-currency"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {wallets.map(w => (
                      <SelectItem key={w.currency} value={w.currency}>
                        {w.currency} (Balance: {w.balance.toFixed(2)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Value</Label>
                <Input
                  type="number" step="0.01" min="0.01"
                  value={buyForm.value}
                  onChange={(e) => setBuyForm({...buyForm, value: e.target.value})}
                  placeholder="0.00"
                  data-testid="buy-voucher-value"
                  required
                />
                <p className="text-xs text-muted-foreground">Amount will be deducted from your wallet</p>
              </div>
              <div className="space-y-2">
                <Label>Expires in (days)</Label>
                <Input
                  type="number" min="1"
                  value={buyForm.expiry_days}
                  onChange={(e) => setBuyForm({...buyForm, expiry_days: e.target.value})}
                  required
                />
              </div>
              <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 text-sm text-blue-800 dark:text-blue-300">
                Anyone with the code can redeem the voucher — perfect for gifts and instant transfers.
              </div>
              <Button type="submit" className="w-full" disabled={buying} data-testid="submit-buy-voucher">
                {buying ? 'Creating...' : 'Buy Voucher'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={!!newVoucher} onOpenChange={(v) => !v && setNewVoucher(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Voucher Created!</DialogTitle></DialogHeader>
            {newVoucher && (
              <div className="space-y-4">
                <div className="text-center py-4">
                  <div className="inline-flex h-16 w-16 rounded-full bg-green-100 dark:bg-green-950 items-center justify-center mb-2">
                    <Sparkles className="h-8 w-8 text-green-600" />
                  </div>
                  <p className="text-3xl font-semibold">{newVoucher.value} {newVoucher.currency}</p>
                </div>
                <div className="p-4 rounded-lg bg-muted font-mono text-center text-lg break-all border-2 border-dashed">
                  {newVoucher.code}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => handleCopy(newVoucher.code)}>
                    <Copy className="h-4 w-4 mr-1" /> Copy Code
                  </Button>
                  <Button className="flex-1" onClick={() => handleShare(newVoucher.code, newVoucher.value, newVoucher.currency)}>
                    <Share2 className="h-4 w-4 mr-1" /> Share
                  </Button>
                </div>
                <p className="text-xs text-center text-muted-foreground">
                  Anyone with this code can redeem it. Share carefully!
                </p>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </FocusedLayout>
  );
};
